<template>
  <div v-if="isVisible" class="auth-modal-overlay" @click.self="closeModal" data-testid="auth-modal">
    <div class="auth-modal">
      <div class="auth-modal-header">
        <h2>{{ isLoginMode ? $t('auth.login') : $t('auth.register') }}</h2>
        <button class="close-button" @click="closeModal" :aria-label="$t('auth.close')" data-testid="auth-modal-close">
          &times;
        </button>
      </div>

      <div class="auth-modal-content">
        <!-- Error message with registration prompt for login failures -->
        <div v-if="authError" class="error-message">
          <div class="error-text">{{ authError }}</div>
          <!-- Show registration prompt only in login mode -->
          <div v-if="isLoginMode" class="register-prompt">
            <p class="prompt-text">
              {{ $t('auth.dontHaveAccount') }}
              <button 
                type="button" 
                class="prompt-link" 
                @click="toggleMode"
                :disabled="isLoading"
              >
                {{ $t('auth.registerHere') }}
              </button>
            </p>
          </div>
        </div>
        <!-- Debug panel (only in development) -->
        <div v-if="isDevelopment" style="font-size: 10px; color: #666; margin-bottom: 10px; padding: 5px; background: #f0f0f0; max-height: 100px; overflow-y: auto;">
          [DEBUG] authError: {{ authError }}, type: {{ typeof authError }}, isVisible: {{ isVisible }}, isLoginMode: {{ isLoginMode }}<br>
          <button @click="loadDebugLogs" style="font-size: 10px; padding: 2px 5px; margin-top: 5px;">Show Persisted Logs</button>
        </div>


        <!-- Verification notice -->
        <div v-if="props.showVerification && !isLoginMode" class="verification-notice">
          <p>{{ $t('auth.verificationNotice') }}</p>
        </div>

        <!-- Verification code input -->
        <div v-if="showVerificationCodeInput" class="verification-code-section">
          <VerificationCodeInput
            :email="verificationEmail"
            :is-loading="isVerifyingCode"
            :error="verificationError"
            :attempts-remaining="verificationAttemptsRemaining"
            :code-expires-at="codeExpiresAt"
            :next-resend-available="nextResendAvailable"
            @code-complete="handleCodeVerified"
            @resend-requested="handleResendCode"
          />
        </div>

        <!-- Login/Register form -->
        <form 
          @submit.prevent="handleSubmit" 
          class="auth-form"
          novalidate
        >
          <div v-if="!isLoginMode" class="form-group">
            <label for="username">{{ $t('auth.username') }}</label>
            <input
              id="username"
              v-model="formData.username"
              type="text"
              :placeholder="$t('auth.usernamePlaceholder')"
              required
              :disabled="isLoading"
            />
          </div>

          <div class="form-group">
            <label for="email">{{ $t('auth.email') }}</label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              :placeholder="$t('auth.emailPlaceholder')"
              required
              :disabled="isLoading"
            />
          </div>

          <div v-if="!isLoginMode" class="form-group">
            <label for="firstName">{{ $t('auth.firstName') }}</label>
            <input
              id="firstName"
              v-model="formData.firstName"
              type="text"
              :placeholder="$t('auth.firstNamePlaceholder')"
              :disabled="isLoading"
            />
          </div>

          <div v-if="!isLoginMode" class="form-group">
            <label for="lastName">{{ $t('auth.lastName') }}</label>
            <input
              id="lastName"
              v-model="formData.lastName"
              type="text"
              :placeholder="$t('auth.lastNamePlaceholder')"
              :disabled="isLoading"
            />
          </div>

          <div class="form-group">
            <label for="password">{{ $t('auth.password') }}</label>
            <input
              id="password"
              v-model="formData.password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="isLoginMode ? $t('auth.passwordPlaceholderLogin') : $t('auth.passwordPlaceholderRegister')"
              required
              :disabled="isLoading"
              @input="onPasswordInput"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? $t('auth.hidePassword') : $t('auth.showPassword')"
            >
              {{ showPassword ? '👁️' : '👁️‍🗨️' }}
            </button>

            <!-- Password requirements checklist (registration mode only) -->
            <div v-if="!isLoginMode" class="password-requirements">
              <p class="password-requirement-hint">{{ $t('auth.passwordRequirement') }}</p>
              <ul v-if="formData.password" class="password-rules-list">
                <li
                  v-for="rule in passwordRules"
                  :key="rule.key"
                  :class="{ 'rule-met': rule.met, 'rule-unmet': !rule.met }"
                >
                  <span class="rule-icon">{{ rule.met ? '✓' : '✗' }}</span>
                  {{ rule.label }}
                </li>
              </ul>
              <!-- Password strength bar -->
              <div v-if="passwordStrength && formData.password" class="password-strength">
                <div class="strength-bar" :class="passwordStrength.class">
                  <div class="strength-fill" :style="{ width: passwordStrength.width + '%' }"></div>
                </div>
                <small class="strength-text">{{ passwordStrength.text }}</small>
              </div>
            </div>
          </div>

          <div v-if="!isLoginMode" class="form-group">
            <label for="confirmPassword">{{ $t('auth.confirmPassword') }}</label>
            <input
              id="confirmPassword"
              v-model="formData.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              :placeholder="$t('auth.confirmPasswordPlaceholder')"
              required
              :disabled="isLoading"
              @input="onConfirmPasswordInput"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showConfirmPassword = !showConfirmPassword"
              :aria-label="showConfirmPassword ? $t('auth.hidePassword') : $t('auth.showPassword')"
            >
              {{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}
            </button>
            <div v-if="confirmPasswordError" class="field-error">{{ confirmPasswordError }}</div>
          </div>

          <div v-if="isLoginMode" class="form-options">
            <!-- Remember Me checkbox hidden - not implemented yet -->
            <label v-if="false" class="remember-me">
              <input type="checkbox" v-model="rememberMe" :disabled="isLoading" />
              Remember me
            </label>
            <button type="button" class="forgot-password" @click="showForgotPassword">
              {{ $t('auth.forgotPassword') }}
            </button>
          </div>

          <button
            type="button"
            @click="handleSubmit"
            class="submit-button"
            :disabled="isLoading || !isFormValid"
            :class="{ loading: isLoading }"
          >
            <span v-if="isLoading">{{ $t('auth.processing') }}</span>
            <span v-else>{{ isLoginMode ? $t('auth.login') : $t('auth.register') }}</span>
          </button>
        </form>

        <!-- Social login section -->
        <div class="social-login-section">
          <SocialLoginButtons 
            :mode="isLoginMode ? 'login' : 'register'"
            @social-login="handleSocialLogin"
            @social-error="handleSocialError"
          />
        </div>

        <!-- Mode toggle -->
        <div class="mode-toggle">
          <p>
            {{ isLoginMode ? $t('auth.dontHaveAccount') : $t('auth.alreadyHaveAccount') }}
            <button type="button" class="toggle-button" @click="toggleMode" :disabled="isLoading">
              {{ isLoginMode ? $t('auth.register') : $t('auth.login') }}
            </button>
          </p>
          
          <!-- Verification link -->
          <div v-if="isLoginMode" class="verification-link">
            <p>
              {{ $t('auth.needVerifyEmail') }}
              <button type="button" class="verification-button" @click="handleVerificationHelp">
                {{ $t('auth.clickHere') }}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Forgot password modal -->
    <div v-if="showForgotPasswordModal" class="forgot-password-modal">
      <div class="forgot-password-content">
        <h3>{{ $t('auth.resetPassword') }}</h3>
        <p>{{ $t('auth.resetPasswordIntro') }}</p>
        
        <form @submit.prevent="handleForgotPassword">
          <div class="form-group">
            <input
              v-model="forgotPasswordEmail"
              type="email"
              :placeholder="$t('auth.emailPlaceholder')"
              required
              :disabled="isLoading"
            />
          </div>
          
          <div class="button-group">
            <button
              type="submit"
              class="submit-button"
              :disabled="isLoading || !forgotPasswordEmail"
            >
              {{ isLoading ? $t('auth.sending') : $t('auth.sendResetLink') }}
            </button>
            <button
              type="button"
              class="cancel-button"
              @click="showForgotPasswordModal = false"
              :disabled="isLoading"
            >
              {{ $t('auth.cancel') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- WeChat QR code modal -->
    <div v-if="showWeChatQRModal" class="wechat-qr-modal">
      <div class="wechat-qr-content">
        <h3>{{ $t('auth.wechatLogin') }}</h3>
        <p>{{ $t('auth.wechatScanPrompt') }}</p>
        
        <div v-if="wechatQRCode" class="qr-code-container">
          <img :src="wechatQRCode" :alt="$t('auth.wechatQRAlt')" class="qr-code" />
          <p class="qr-status">{{ wechatStatus }}</p>
        </div>
        
        <div v-else class="loading-qr">
          <p>{{ $t('auth.generatingQR') }}</p>
        </div>
        
        <div class="button-group">
          <button
            type="button"
            class="cancel-button"
            @click="closeWeChatQRModal"
            :disabled="isLoading"
          >
            {{ $t('auth.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../stores/auth';
import SocialLoginButtons from './SocialLoginButtons.vue';
import VerificationCodeInput from './VerificationCodeInput.vue';
import api from '../services/api';
import { toast } from 'vue3-toastify';
import { translateError } from '../utils/errorTranslator';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  defaultMode: {
    type: String,
    default: 'login', // 'login' or 'register'
    validator: (value) => ['login', 'register'].includes(value)
  },
  showVerification: {
    type: Boolean,
    default: false
  },
  verificationToken: {
    type: String,
    default: null
  },
  // Pipeline context for authentication during pipeline flow
  pipelineContext: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close', 'success', 'verification-needed', 'verification-completed', 'pipeline-linked']);

const authStore = useAuthStore();
const { t } = useI18n();

// Development mode check (must be declared early - used in template and throughout component)
const isDevelopment = import.meta.env.DEV;

// Load and display persisted debug logs on mount (development only)
const loadDebugLogs = () => {
  if (!isDevelopment) return;
  
  try {
    const logs = JSON.parse(localStorage.getItem('authDebugLogs') || '[]');
    if (logs.length > 0) {
      console.log('[DEBUG] AuthModal: Loaded persisted debug logs from localStorage:', logs);
      // Optionally clear old logs after displaying
      // localStorage.removeItem('authDebugLogs');
    }
  } catch (e) {
    console.error('Failed to load debug logs:', e);
  }
};

// Load logs when component is mounted (development only)
// Note: onMounted must be called unconditionally, but we check isDevelopment inside
onMounted(() => {
  if (isDevelopment) {
    loadDebugLogs();
  }
});

// Form state
const isLoginMode = ref(props.defaultMode === 'login');
const formData = ref({
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  confirmPassword: ''
});
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const rememberMe = ref(true);

// Verification state
const showVerificationCodeInput = ref(false);
const verificationEmail = ref('');
const isVerifyingCode = ref(false);
const verificationError = ref(null);
const verificationAttemptsRemaining = ref(null);
const codeExpiresAt = ref(null);
const nextResendAvailable = ref(null);

// Forgot password state
const showForgotPasswordModal = ref(false);
const forgotPasswordEmail = ref('');

// WeChat QR code state
const showWeChatQRModal = ref(false);
const wechatQRCode = ref(null);
const wechatStatus = ref('Waiting for scan...');
const wechatPollingInterval = ref(null);

// Password validation state
const passwordErrors = ref({});
const passwordStrength = ref(null);
const confirmPasswordError = ref(null);

// Password validation rules - real-time checking
const passwordRules = computed(() => {
  const p = formData.value.password || '';
  try {
    return [
      { key: 'minLength', met: p.length >= 8, label: t('auth.rule.minLength') },
      { key: 'uppercase', met: /[A-Z]/.test(p), label: t('auth.rule.uppercase') },
      { key: 'lowercase', met: /[a-z]/.test(p), label: t('auth.rule.lowercase') },
      { key: 'number', met: /\d/.test(p), label: t('auth.rule.number') },
      { key: 'specialChar', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p), label: t('auth.rule.specialChar') },
    ];
  } catch (e) {
    console.warn('[AuthModal] Failed to compute passwordRules (i18n error):', e.message);
    // Fallback with hardcoded labels if i18n fails
    return [
      { key: 'minLength', met: p.length >= 8, label: '≥8 chars' },
      { key: 'uppercase', met: /[A-Z]/.test(p), label: 'A-Z' },
      { key: 'lowercase', met: /[a-z]/.test(p), label: 'a-z' },
      { key: 'number', met: /\d/.test(p), label: '0-9' },
      { key: 'specialChar', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p), label: '!@#$%' },
    ];
  }
});

const allPasswordRulesMet = computed(() => {
  const rules = passwordRules.value;
  return Array.isArray(rules) && rules.every(r => r.met);
});

// Real-time password validation handler
const onPasswordInput = () => {
  const password = formData.value.password;
  if (!password) {
    passwordStrength.value = null;
    return;
  }

  const rules = passwordRules.value;
  const metCount = Array.isArray(rules) ? rules.filter(r => r.met).length : 0;

  if (password.length < 8) {
    passwordStrength.value = { class: 'weak', width: 20, text: t('auth.passwordStrength.weak') };
  } else if (metCount < 3) {
    passwordStrength.value = { class: 'weak', width: 40, text: t('auth.passwordStrength.weak') };
  } else if (metCount === 3) {
    passwordStrength.value = { class: 'medium', width: 60, text: t('auth.passwordStrength.medium') };
  } else if (metCount === 4) {
    passwordStrength.value = { class: 'medium', width: 75, text: t('auth.passwordStrength.good') };
  } else {
    passwordStrength.value = { class: 'strong', width: 100, text: t('auth.passwordStrength.strong') };
  }

  // Also validate confirm password match if already filled
  if (formData.value.confirmPassword) {
    onConfirmPasswordInput();
  }
};

// Real-time confirm password validation
const onConfirmPasswordInput = () => {
  if (formData.value.confirmPassword && formData.value.password !== formData.value.confirmPassword) {
    confirmPasswordError.value = t('errors.passwordMismatch');
  } else {
    confirmPasswordError.value = null;
  }
};

// Computed properties
const isLoading = computed(() => authStore.isLoading);
const authError = computed(() => {
  const error = authStore.authError;
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.authError computed: Accessed', {
      error,
      errorType: typeof error,
      isNull: error === null,
      isUndefined: error === undefined,
      isEmpty: error === '',
      isVisible: props.isVisible,
      stackTrace: new Error().stack
    });
  }
  // Translate backend error messages to current locale
  if (error && typeof error === 'string') {
    return translateError(error, t);
  }
  return error;
});
const isFormValid = computed(() => {
  if (isLoginMode.value) {
    return formData.value.email && formData.value.password;
  } else {
    return (
      formData.value.username &&
      formData.value.email &&
      formData.value.password &&
      formData.value.confirmPassword &&
      formData.value.password === formData.value.confirmPassword &&
      allPasswordRulesMet.value &&
      !confirmPasswordError.value
    );
  }
});

// Watch for defaultMode changes to keep isLoginMode in sync
watch(() => props.defaultMode, (newMode) => {
  isLoginMode.value = newMode === 'login';
  // Reset form when mode changes while modal is visible
  if (props.isVisible) {
    resetForm();
  }
});

// Watch for visibility changes
watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    // Sync mode when modal opens (defensive approach)
    isLoginMode.value = props.defaultMode === 'login';
    resetForm();
  }
});

// Watch for auth success
watch(() => authStore.isAuthenticated, (newVal) => {
  if (newVal) {
    handleAuthSuccess();
  }
});

// Watch for verification token
watch(() => props.verificationToken, (newToken) => {
  if (newToken && props.isVisible) {
    handleVerificationToken(newToken);
  }
});

// Watch for auth error changes (development only)
if (isDevelopment) {
  watch(() => authStore.authError, (newError, oldError) => {
    console.log('[DEBUG] AuthModal: authError changed', {
      oldError,
      newError,
      oldType: typeof oldError,
      newType: typeof newError,
      isVisible: props.isVisible,
      isLoginMode: isLoginMode.value,
      stackTrace: new Error().stack
    });
  }, { immediate: true });

  // Watch for modal visibility changes
  watch(() => props.isVisible, (newVal, oldVal) => {
    console.log('[DEBUG] AuthModal: Visibility changed', {
      oldVal,
      newVal,
      authError: authStore.authError,
      isLoginMode: isLoginMode.value
    });
  });
}

// Methods
const closeModal = () => {
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.closeModal: Called', {
      currentError: authStore.authError
    });
  }
  emit('close');
  resetForm();
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.closeModal: After resetForm, error =', authStore.authError);
  }
};

const toggleMode = () => {
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.toggleMode: Called', {
      currentMode: isLoginMode.value,
      currentError: authStore.authError
    });
  }
  isLoginMode.value = !isLoginMode.value;
  resetForm();
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.toggleMode: After toggle and reset, newMode =', isLoginMode.value, 'error =', authStore.authError);
  }
};

const resetForm = () => {
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.resetForm: Called', {
      currentError: authStore.authError,
      isVisible: props.isVisible
    });
  }
  formData.value = {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  };
  showPassword.value = false;
  showConfirmPassword.value = false;
  passwordStrength.value = null;
  confirmPasswordError.value = null;
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.resetForm: Clearing error via setError(null)');
  }
  authStore.setError(null);
  showVerificationCodeInput.value = false;
  verificationError.value = null;
  verificationAttemptsRemaining.value = null;
  codeExpiresAt.value = null;
  nextResendAvailable.value = null;
  if (isDevelopment) {
    console.log('[DEBUG] AuthModal.resetForm: After reset, error =', authStore.authError);
  }
};

const handleSubmit = async (event) => {
  // CRITICAL: Prevent default form submission to avoid page refresh
  if (event) {
    event.preventDefault();
    event.stopPropagation();
    if (isDevelopment) {
      console.log('[DEBUG] AuthModal.handleSubmit: Event prevented', {
        defaultPrevented: event.defaultPrevented,
        isPropagationStopped: event.cancelBubble
      });
    }
  }
  
  // Log to localStorage to persist through page refresh (development only)
  if (isDevelopment) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action: 'handleSubmit',
      isLoginMode: isLoginMode.value,
      isFormValid: isFormValid.value,
      email: formData.value.email
    };
    console.log('[DEBUG] AuthModal.handleSubmit: Called', logEntry);
    try {
      const existingLogs = JSON.parse(localStorage.getItem('authDebugLogs') || '[]');
      existingLogs.push(logEntry);
      localStorage.setItem('authDebugLogs', JSON.stringify(existingLogs.slice(-50))); // Keep last 50
    } catch (e) {
      console.error('Failed to save log to localStorage:', e);
    }
  }
  
  if (!isFormValid.value) {
    if (isDevelopment) {
      console.log('[DEBUG] AuthModal.handleSubmit: Form invalid, returning');
    }
    return false; // Explicitly return false
  }

  if (isLoginMode.value) {
    // Login
    if (isDevelopment) {
      console.log('[DEBUG] AuthModal.handleSubmit: Starting login for', formData.value.email);
    }
    const result = await authStore.login(formData.value.email, formData.value.password);
    
    if (isDevelopment) {
      console.log('[DEBUG] AuthModal.handleSubmit: Login result received', {
        success: result.success,
        hasError: !!result.error,
        error: result.error,
        authStoreError: authStore.authError,
        resultKeys: Object.keys(result)
      });
    }
    
    if (result.success) {
      if (isDevelopment) {
        console.log('[DEBUG] AuthModal.handleSubmit: Login successful');
      }
      // Check if verification is needed
      if (result.needsVerification) {
        if (isDevelopment) {
          console.log('[DEBUG] AuthModal.handleSubmit: Verification needed');
        }
        emit('verification-needed', {
          email: formData.value.email,
          user: result.user,
          message: result.verificationMessage
        });
      } else {
        if (isDevelopment) {
          console.log('[DEBUG] AuthModal.handleSubmit: Calling handleAuthSuccess');
        }
        handleAuthSuccess();
      }
    } else {
      if (isDevelopment) {
        console.log('[DEBUG] AuthModal.handleSubmit: Login failed, handling error', {
          resultError: result.error,
          authStoreError: authStore.authError,
          authStoreErrorType: typeof authStore.authError
        });
      }
      
      // Explicit error handling for login failures
      // Error is already set in store via authStore.login(), but ensure it's displayed
      // The error should already be visible via authError computed property
      // Add explicit check to ensure error state is properly set
      if (result.error && !authStore.authError) {
        if (isDevelopment) {
          console.log('[DEBUG] AuthModal.handleSubmit: Setting error explicitly (fallback)', result.error);
        }
        // Fallback: if error wasn't set in store, set it explicitly
        authStore.setError(result.error);
      } else if (!result.error && !authStore.authError) {
        if (isDevelopment) {
          console.warn('[DEBUG] AuthModal.handleSubmit: No error in result and no error in store!');
        }
      } else {
        if (isDevelopment) {
          console.log('[DEBUG] AuthModal.handleSubmit: Error already set in store:', authStore.authError);
        }
      }
      
      if (isDevelopment) {
        console.log('[DEBUG] AuthModal.handleSubmit: Final authStore.authError after handling:', authStore.authError);
      }
      // Error will be displayed via existing error-message div
      // No need to close modal or reset form - let user see the error and try again
      
      // Log error state to localStorage (development only)
      if (isDevelopment) {
        try {
          const errorLog = {
            timestamp: new Date().toISOString(),
            action: 'loginError',
            resultError: result.error,
            authStoreError: authStore.authError,
            finalError: authStore.authError || result.error
          };
          const existingLogs = JSON.parse(localStorage.getItem('authDebugLogs') || '[]');
          existingLogs.push(errorLog);
          localStorage.setItem('authDebugLogs', JSON.stringify(existingLogs.slice(-50)));
        } catch (e) {
          console.error('Failed to save error log:', e);
        }
      }
    }
    
    // Explicitly return false to prevent any form submission
    return false;
  } else {
    // Register
    const userData = {
      username: formData.value.username,
      email: formData.value.email,
      password: formData.value.password,
      profile: {
        firstName: formData.value.firstName,
        lastName: formData.value.lastName
      }
    };
    
    const result = await authStore.register(userData);
    if (result.success) {
      // Check if verification is needed
      if (result.needsVerification) {
        // Check if email was queued
        if (result.emailQueued || result.queuePosition) {
          // Email is queued - show queue status
          showVerificationCodeInput.value = true;
          verificationEmail.value = formData.value.email;
          verificationError.value = null;
          verificationAttemptsRemaining.value = null;
          
          // Store queue info
          const queueInfo = {
            queued: true,
            position: result.queuePosition,
            estimatedWaitTime: result.estimatedWaitTime
          };
          
          // Emit verification needed event with queue info
          emit('verification-needed', {
            email: formData.value.email,
            user: result.user,
            message: result.verificationMessage || 'Your verification email is in the queue.',
            queueInfo
          });
        } else {
          // Normal flow - email sent immediately
          showVerificationCodeInput.value = true;
          verificationEmail.value = formData.value.email;
          verificationError.value = null;
          verificationAttemptsRemaining.value = null;
          
          // Set code expiration (10 minutes from now)
          const expiresAt = new Date();
          expiresAt.setMinutes(expiresAt.getMinutes() + 10);
          codeExpiresAt.value = expiresAt.toISOString();
          
          // Emit verification needed event
          emit('verification-needed', {
            email: formData.value.email,
            user: result.user,
            message: result.verificationMessage
          });
        }
      } else {
        handleAuthSuccess();
      }
    } else {
      // Explicit error handling for registration failures
      if (result.error && !authStore.authError) {
        authStore.setError(result.error);
      }
      // Error will be displayed via existing error-message div
    }
  }
};

const handleAuthSuccess = async () => {
  // If there's pipeline context, link it to the authenticated user
  if (props.pipelineContext && props.pipelineContext.pipelineId) {
    await linkPipelineToUser(props.pipelineContext.pipelineId);
  }
  
  emit('success');
  closeModal();
};

const linkPipelineToUser = async (pipelineId) => {
  try {
    const result = await api.linkPipelineSession(pipelineId);
    if (result.success) {
      console.log('Pipeline linked to user:', pipelineId);
      emit('pipeline-linked', { pipelineId, success: true });
      toast.success('Your pipeline has been linked to your account');
    } else {
      console.warn('Failed to link pipeline:', result.error);
      emit('pipeline-linked', { pipelineId, success: false, error: result.error });
    }
  } catch (error) {
    console.error('Error linking pipeline:', error);
    emit('pipeline-linked', { pipelineId, success: false, error: error.message });
  }
};

const handleVerificationToken = async (token) => {
  try {
    const result = await authStore.verifyEmail(token);
    
    if (result.success) {
      // Show success message
      authStore.setError(null);
      
      // If user is logged in, update their status
      if (authStore.isAuthenticated) {
        // Emit verification completed event
        emit('verification-completed', {
          user: result.user,
          message: result.message
        });
        
        // Show success and close modal after delay
        setTimeout(() => {
          handleAuthSuccess();
        }, 2000);
      } else {
        // If not logged in, show success message and switch to login
        authStore.setError('Email verified successfully! Please login to continue.');
        isLoginMode.value = true;
        resetForm();
      }
    } else {
      authStore.setError(result.error);
    }
  } catch (error) {
    authStore.setError('Failed to verify email. Please try again.');
  }
};

const showForgotPassword = () => {
  showForgotPasswordModal.value = true;
};

const handleForgotPassword = async () => {
  if (!forgotPasswordEmail.value) return;

  const result = await authStore.requestPasswordReset(forgotPasswordEmail.value);
  if (result.success) {
    alert('Password reset link has been sent to your email.');
    showForgotPasswordModal.value = false;
    forgotPasswordEmail.value = '';
  }
};

const socialLogin = async (provider) => {
  if (provider === 'wechat') {
    await handleWeChatLogin();
    return;
  }

  // For other providers, redirect to OAuth URL
  const oauthUrls = {
    google: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/google`,
    github: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/github`,
    linkedin: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/linkedin`
  };

  const url = oauthUrls[provider];
  if (url) {
    window.location.href = url;
  }
};

const handleWeChatLogin = async () => {
  try {
    showWeChatQRModal.value = true;
    
    // Generate QR code
    const response = await authStore.generateWeChatQRCode();
    wechatQRCode.value = response.qrCodeUrl;
    
    // Start polling for status
    startWeChatPolling(response.qrCodeId);
  } catch (error) {
    authStore.setError('Failed to generate WeChat QR code: ' + error.message);
    showWeChatQRModal.value = false;
  }
};

const startWeChatPolling = (qrCodeId) => {
  if (wechatPollingInterval.value) {
    clearInterval(wechatPollingInterval.value);
  }

  wechatPollingInterval.value = setInterval(async () => {
    try {
      const status = await authStore.checkWeChatQRCodeStatus(qrCodeId);
      wechatStatus.value = status.message;

      if (status.status === 'scanned') {
        // QR code scanned, wait for confirmation
        wechatStatus.value = 'Confirmed! Logging in...';
      } else if (status.status === 'confirmed') {
        // Login successful
        clearInterval(wechatPollingInterval.value);
        wechatPollingInterval.value = null;
        
        // Update auth store with tokens
        authStore.setTokens(status.tokens);
        authStore.setUser(status.user);
        
        // Close modals and show success
        showWeChatQRModal.value = false;
        handleAuthSuccess();
      } else if (status.status === 'expired') {
        // QR code expired
        clearInterval(wechatPollingInterval.value);
        wechatPollingInterval.value = null;
        wechatStatus.value = 'QR code expired. Please try again.';
      }
    } catch (error) {
      console.error('WeChat polling error:', error);
    }
  }, 2000); // Poll every 2 seconds
};

const closeWeChatQRModal = () => {
  if (wechatPollingInterval.value) {
    clearInterval(wechatPollingInterval.value);
    wechatPollingInterval.value = null;
  }
  showWeChatQRModal.value = false;
  wechatQRCode.value = null;
  wechatStatus.value = 'Waiting for scan...';
};

const handleSocialLogin = async (event) => {
  const { provider, success, message } = event;
  
  if (success) {
    // For demo purposes, show success message
    alert(`${message}\n\nNote: In production, this would complete the ${provider} OAuth flow.`);
    
    // Simulate successful login
    const mockUser = {
      id: 'mock_user_' + Date.now(),
      email: `${provider}_user@example.com`,
      username: `${provider}_user`,
      profile: {
        firstName: provider.charAt(0).toUpperCase() + provider.slice(1),
        lastName: 'User'
      }
    };
    
    // Update auth store with mock data for demo
    authStore.setUser(mockUser);
    authStore.setAuthenticated(true);
    
    // Close modal and emit success
    handleAuthSuccess();
  }
};

const handleSocialError = (event) => {
  const { provider, error } = event;
  authStore.setError(`${provider} login failed: ${error}`);
};

const handleVerificationHelp = () => {
  // Show verification help information
  authStore.setError('Please check your email for the verification link. If you didn\'t receive it, try registering again or contact support.');
};

const handleCodeVerified = async (code) => {
  if (!verificationEmail.value) return;
  
  isVerifyingCode.value = true;
  verificationError.value = null;
  
  try {
    const result = await authStore.verifyCodeAndLogin(code, verificationEmail.value);
    
    if (result.success) {
      // Auto-logged in!
      toast.success('Email verified! You are now logged in.');
      
      // If there's pipeline context, link it to the authenticated user
      if (props.pipelineContext && props.pipelineContext.pipelineId) {
        await linkPipelineToUser(props.pipelineContext.pipelineId);
      }
      
      // Hide code input
      showVerificationCodeInput.value = false;
      
      // Emit success and close modal
      emit('success');
      closeModal();
    } else {
      // Show error
      verificationError.value = result.error;
      verificationAttemptsRemaining.value = result.attemptsRemaining;
      
      // If new code was sent, update expiration
      if (result.newCodeSent) {
        const expiresAt = new Date();
        expiresAt.setMinutes(expiresAt.getMinutes() + 10);
        codeExpiresAt.value = expiresAt.toISOString();
        toast.info('A new verification code has been sent to your email.');
      }
    }
  } catch (error) {
    verificationError.value = 'Failed to verify code. Please try again.';
  } finally {
    isVerifyingCode.value = false;
  }
};

const handleResendCode = async () => {
  if (!verificationEmail.value) return;
  
  try {
    const result = await authStore.resendVerificationCode(verificationEmail.value);
    
    if (result.success) {
      verificationError.value = null;
      
      // Update expiration time
      if (result.codeExpiresAt) {
        codeExpiresAt.value = result.codeExpiresAt;
      }
      
      // Update next resend time
      if (result.nextResendAvailable) {
        nextResendAvailable.value = result.nextResendAvailable;
      }
      
      toast.success('New verification code sent! Please check your inbox.');
    } else {
      verificationError.value = result.error || 'Failed to resend verification code.';
      
      // Update next resend time if provided
      if (result.nextResendAvailable) {
        nextResendAvailable.value = result.nextResendAvailable;
      }
    }
  } catch (error) {
    verificationError.value = 'Failed to resend verification code. Please try again.';
  }
};
</script>

<style scoped>
.auth-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.auth-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.auth-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.auth-modal-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #111827;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.auth-modal-content {
  padding: 24px;
}

.error-message {
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.error-text {
  margin-bottom: 8px;
}

.register-prompt {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #fecaca;
}

.prompt-text {
  margin: 0;
  font-size: 13px;
  color: #991b1b;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.prompt-link {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
  font-size: 13px;
  transition: color 0.2s;
}

.prompt-link:hover:not(:disabled) {
  color: #991b1b;
  text-decoration: none;
}

.prompt-link:disabled {
  color: #9ca3af;
  cursor: not-allowed;
  text-decoration: none;
}


.verification-notice {
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0369a1;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.verification-notice p {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.verification-instructions {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.verification-instructions h3 {
  margin: 0 0 12px 0;
  font-size: 18px;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 8px;
}

.verification-instructions p {
  margin: 0 0 12px 0;
  color: #92400e;
  font-size: 14px;
  line-height: 1.5;
}

.verification-tips {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  padding: 12px;
  margin: 16px 0;
}

.verification-tips p {
  margin: 0 0 8px 0;
  font-weight: 600;
}

.verification-tips ul {
  margin: 0;
  padding-left: 20px;
}

.verification-tips li {
  margin-bottom: 4px;
  color: #92400e;
  font-size: 13px;
}

.verification-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.resend-button {
  flex: 1;
  padding: 10px 16px;
  background-color: #f59e0b;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.resend-button:hover:not(:disabled) {
  background-color: #d97706;
}

.resend-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.close-instructions-button {
  flex: 1;
  padding: 10px 16px;
  background-color: #e5e7eb;
  color: #374151;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-instructions-button:hover:not(:disabled) {
  background-color: #d1d5db;
}

.close-instructions-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.verification-link {
  margin-top: 12px;
  font-size: 14px;
  color: #6b7280;
}

.verification-link p {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.verification-button {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
}

.verification-button:hover {
  color: #2563eb;
}

.auth-form {
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 20px;
  position: relative;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group input:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 38px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  color: #6b7280;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 14px;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
}

.remember-me input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.forgot-password {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  text-decoration: underline;
}

.forgot-password:hover {
  color: #2563eb;
}

.forgot-password:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.submit-button {
  width: 100%;
  padding: 14px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-button:hover:not(:disabled) {
  background-color: #2563eb;
}

.submit-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.submit-button.loading {
  background-color: #2563eb;
  cursor: wait;
}

.social-login-section {
  margin: 24px 0;
}

.divider {
  display: flex;
  align-items: center;
  margin: 20px 0;
  color: #6b7280;
  font-size: 14px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: #e5e7eb;
}

.divider span {
  padding: 0 12px;
}

.social-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.social-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s, border-color 0.2s;
}

.social-button:hover:not(:disabled) {
  background-color: #f9fafb;
}

.social-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.social-button.google {
  border-color: #db4437;
  color: #db4437;
}

.social-button.google:hover:not(:disabled) {
  background-color: #fef2f2;
}

.social-button.github {
  border-color: #333;
  color: #333;
}

.social-button.github:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.social-button.linkedin {
  border-color: #0077b5;
  color: #0077b5;
}

.social-button.linkedin:hover:not(:disabled) {
  background-color: #f0f9ff;
}

.social-button.wechat {
  border-color: #07c160;
  color: #07c160;
}

.social-button.wechat:hover:not(:disabled) {
  background-color: #f0fdf4;
}

.social-icon {
  font-weight: bold;
  font-size: 12px;
}

.mode-toggle {
  text-align: center;
  margin-top: 24px;
  color: #6b7280;
  font-size: 14px;
}

.toggle-button {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
  margin-left: 4px;
}

.toggle-button:hover:not(:disabled) {
  text-decoration: underline;
}

.toggle-button:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.forgot-password-modal,
.wechat-qr-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.forgot-password-content,
.wechat-qr-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.forgot-password-content h3,
.wechat-qr-content h3 {
  margin: 0 0 12px 0;
  font-size: 20px;
  color: #111827;
}

.forgot-password-content p,
.wechat-qr-content p {
  margin: 0 0 20px 0;
  color: #6b7280;
  font-size: 14px;
}

.button-group {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.cancel-button {
  flex: 1;
  padding: 12px;
  background-color: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.cancel-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qr-code-container {
  text-align: center;
  margin: 20px 0;
}

.qr-code {
  width: 200px;
  height: 200px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: white;
}

.qr-status {
  margin-top: 12px;
  color: #6b7280;
  font-size: 14px;
}

.loading-qr {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

/* Password requirements */
.password-requirements {
  margin-top: 8px;
}

.password-requirement-hint {
  margin: 0 0 6px 0;
  font-size: 12px;
  color: #6b7280;
}

.password-rules-list {
  list-style: none;
  padding: 0;
  margin: 0 0 8px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 12px;
}

.password-rules-list li {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  transition: color 0.2s;
}

.rule-icon {
  font-size: 11px;
  font-weight: 700;
  width: 14px;
  text-align: center;
  flex-shrink: 0;
}

.rule-met {
  color: #10b981;
}

.rule-unmet {
  color: #9ca3af;
}

/* Password strength bar */
.password-strength {
  margin-top: 6px;
}

.strength-bar {
  height: 4px;
  background-color: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.strength-fill {
  height: 100%;
  transition: width 0.3s, background-color 0.3s;
}

.strength-bar.weak .strength-fill {
  background-color: #dc2626;
}

.strength-bar.medium .strength-fill {
  background-color: #f59e0b;
}

.strength-bar.strong .strength-fill {
  background-color: #10b981;
}

.strength-text {
  color: #6b7280;
  font-size: 12px;
}

/* Field-level error */
.field-error {
  color: #dc2626;
  font-size: 13px;
  margin-top: 4px;
}
</style>
